import { FormattedMessage, useIntl } from "react-intl";
import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import useExploreFetcher from "../../actions/explore/useExploreFetcher.tsx";
import useAssetActivate from "../../actions/explore/activate/useAssetActivate.tsx";
import { useEffect, useRef } from "react";
import styles from "./SearchResultBorrowDialog.module.scss";
import useAssetAvailability, {
  BorrowStatus,
} from "../../actions/explore/activate/useAssetAvailability.tsx";

interface Props {
  onRequestClose: () => void;
  asset: VST.Bridge.Asset;
}

enum CannotBorrowReason {
  BorrowingLimitExceeded = "BorrowingLimitExceeded",
  NoCopiesAvailable = "NoCopiesAvailable",
  AlreadyBorrowing = "AlreadyBorrowing",
}

interface MessageParams {
  title: string;
  description: string;
  supplemental: string;
}

function CannotBorrowDialog({
  reason,
  onRequestClose,
}: {
  reason: CannotBorrowReason;
  onRequestClose: () => void;
}) {
  const intl = useIntl();
  const messages: Record<string, MessageParams> = {
    BorrowingLimitExceeded: {
      title: intl.formatMessage({ defaultMessage: "Book Unavailable" }),
      description: intl.formatMessage({
        defaultMessage: "Borrowing limit reached.",
      }),
      supplemental: intl.formatMessage({
        defaultMessage:
          "Return another book in order to borrow this book and view search result.",
      }),
    },
    NoCopiesAvailable: {
      title: intl.formatMessage({ defaultMessage: "Book Unavailable" }),
      description: intl.formatMessage({
        defaultMessage: "No copies available; please try again later.",
      }),
      supplemental: intl.formatMessage({
        defaultMessage: "Book must be borrowed in order to view search result.",
      }),
    },
    // edge case if they click borrow and a search hit immediately after
    AlreadyBorrowing: {
      title: intl.formatMessage({ defaultMessage: "Please wait" }),
      description: intl.formatMessage({
        defaultMessage: "Book is in the process of being borrowed.",
      }),
      supplemental: intl.formatMessage({
        defaultMessage: "Please try again in a moment.",
      }),
    },
  };

  const message = messages[reason];

  return (
    <Dialog
      size="small"
      title={message.title}
      onRequestClose={onRequestClose}
      primaryButton={{
        label: intl.formatMessage({ defaultMessage: "Got it" }),
        onClick: onRequestClose,
      }}
    >
      <p>{message.description}</p>
      <p className={styles.additionalPara}>{message.supplemental}</p>
    </Dialog>
  );
}

const getCantBorrowStatus = (
  isAlreadySubmitting: boolean,
  borrowStatus: BorrowStatus
) => {
  if (isAlreadySubmitting) {
    return CannotBorrowReason.AlreadyBorrowing;
  } else if (borrowStatus === BorrowStatus.NoCopies) {
    // priority, no point in saying return a book if there isn't a copy available when they do
    return CannotBorrowReason.NoCopiesAvailable;
  } else if (
    borrowStatus === BorrowStatus.NoCredits ||
    borrowStatus === BorrowStatus.NoCreditsOrCopies
  ) {
    return CannotBorrowReason.BorrowingLimitExceeded;
  }
};

export default function SearchResultBorrowDialog({
  asset,
  onRequestClose,
}: Props) {
  const intl = useIntl();
  const { activeFetcher, activationComplete } = useExploreFetcher(asset);
  const activateAsset = useAssetActivate(asset);
  const { getBorrowStatus } = useAssetAvailability(asset);
  const borrowStatus = getBorrowStatus();
  const activating =
    activeFetcher?.state === "submitting" || !!activationComplete;
  // capture at start, don't want to flip midway
  const cantBorrowAtStart = useRef(
    getCantBorrowStatus(activeFetcher?.state === "submitting", borrowStatus)
  );

  useEffect(() => {
    if (activeFetcher && activationComplete && !cantBorrowAtStart.current) {
      onRequestClose();
    }
  }, [activationComplete, activeFetcher, onRequestClose]);

  if (cantBorrowAtStart.current) {
    return (
      <CannotBorrowDialog
        reason={cantBorrowAtStart.current}
        onRequestClose={onRequestClose}
      />
    );
  }

  return (
    <Dialog
      canClose={!activating}
      size="small"
      title={intl.formatMessage({ defaultMessage: "Borrow Book?" })}
      onRequestClose={onRequestClose}
      primaryButton={{
        label: intl.formatMessage({ defaultMessage: "Borrow" }),
        loading: activating,
        onClick: () => activateAsset(),
      }}
      secondaryButton={{
        onClick: activating ? undefined : onRequestClose,
        label: intl.formatMessage({ defaultMessage: "Cancel" }),
      }}
    >
      <p>
        <FormattedMessage defaultMessage="You must borrow this book in order to view the search result." />
      </p>
    </Dialog>
  );
}
